import React, { useContext, useEffect, useState } from "react";
import { Button, Chip, Popover, Typography } from "@material-ui/core";
import EventBusyOutlinedIcon from "@material-ui/icons/EventBusyOutlined";
import EventAvailableOutlinedIcon from "@material-ui/icons/EventAvailableOutlined";
import { useCompany } from "../../Context/CompanyContext";
import { useCart } from "../../Context/CartContext";
import { StockContext } from "../../Context/StockContext";
import { neededInStockUnit, validPicks } from "../../utils/batchPicks";
import BatchPickerDialog from "./BatchPickerDialog";
import Tip from "./Tip";
import { previewBatchAllocation } from "../../api";
import { daysUntilExpiry, formatExpiry } from "./ExpiryChip";

const TONES = {
  expired: { background: "#fee2e2", color: "#b91c1c", border: "#fecaca" },
  near: { background: "#fef3c7", color: "#b45309", border: "#fde68a" },
  ok: { background: "#dcfce7", color: "#15803d", border: "#bbf7d0" },
  none: { background: "#f1f5f9", color: "#475569", border: "#e2e8f0" },
};

const hint = (days) =>
  days === null ? "no expiry date" : days < 0 ? `expired ${Math.abs(days)}d ago` : days === 0 ? "expires today" : `${days}d left`;

// Short form for the narrow cart column: "03 Oct" (year only when it is not this year).
const shortDate = (d) => {
  const e = new Date(d);
  const sameYear = e.getUTCFullYear() === new Date().getFullYear();
  return e.toLocaleDateString(undefined, { day: "2-digit", month: "short", ...(sameYear ? {} : { year: "numeric" }), timeZone: "UTC" });
};
const shortHint = (days) => (days < 0 ? `${Math.abs(days)}d ago` : days === 0 ? "today" : `${days}d left`);

const qtyText = (n) => String(Math.round(Number(n) * 100) / 100);

// POS cart row: which batch this line will be sold from and when it expires,
// updated as the quantity changes. Click for the full batch breakdown.
// Renders nothing unless Batch & Expiry is on and the item tracks expiry.
const CartExpiryInfo = ({ item, canPick = true }) => {
  const { company } = useCompany();
  const { setCartItems } = useCart();
  const { getConvertedQuantity } = useContext(StockContext);
  const [plan, setPlan] = useState(null);
  const [anchor, setAnchor] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);

  const active = company?.batchExpiryEnabled === true && item?.trackExpiry === true && item?.toggleStock === true && !item?.isStaffReq;
  const nearDays = Number(company?.nearExpiryDays) || 30;
  const qty = Number(item?.quantity) || 0;
  const unitId = item?.units?.sale?.unit?._id || item?.units?.sale?.unit;

  useEffect(() => {
    if (!active || !item?._id || qty <= 0) {
      setPlan(null);
      return undefined;
    }
    let cancelled = false;
    // Short delay so typing / tapping + does not fire a request per keystroke.
    const timer = setTimeout(async () => {
      try {
        const { data } = await previewBatchAllocation({ itemId: item._id, qty, unitId });
        if (!cancelled) setPlan(data);
      } catch (error) {
        if (!cancelled) setPlan(null); // advisory only - the sale itself is checked on the server
      }
    }, 350);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [active, item?._id, qty, unitId]);

  if (!active || !plan?.tracked) return null;

  // Batches the cashier chose win over the automatic plan, while they still add up.
  const needed = neededInStockUnit(getConvertedQuantity, item);
  const picked = canPick ? validPicks(item, needed) : null;
  const allocations = picked || plan.allocations || [];
  const first = allocations[0];
  const days = first ? daysUntilExpiry(first.expiryDate) : null;
  const level = plan.blocked && !picked
    ? "expired"
    : !first
    ? "none"
    : days === null
    ? "none"
    : days < 0
    ? "expired"
    : days <= nearDays
    ? "near"
    : "ok";
  const tone = TONES[level];
  const extra = allocations.length > 1 ? ` +${allocations.length - 1}` : "";
  const label = plan.blocked && !picked
    ? "Expired only"
    : !first
    ? "No batch stock"
    : days === null
    ? `No expiry date${extra}`
    : days === 0
    ? `Exp today${extra}`
    : `${picked ? "Chosen · " : ""}Exp ${shortDate(first.expiryDate)} · ${shortHint(days)}${extra}`;

  const stockUnit = item?.units?.stock?.unit?.abbreviation || "";
  const mode = plan.expiredSaleMode || company?.expiredSaleMode || "allow";
  const setPicks = (picks) => {
    setCartItems((prev) =>
      prev.map((line) => (String(line?._id) === String(item._id) ? { ...line, batchPicks: picks.length ? picks : undefined } : line))
    );
    setPickerOpen(false);
    setAnchor(null);
  };
  const modeText =
    mode === "block"
      ? "Expired stock can't be sold."
      : mode === "warn"
      ? "You will be asked to confirm before expired stock is sold."
      : "Expired stock sells like any other.";

  // Tooltip: which batch, when it expires, how much comes from it, what the store rule means.
  const qtyHere = first ? qtyText(first.qty) : "";
  const tipTitle = plan.blocked && !picked
    ? "Only expired stock can cover this"
    : !first
    ? "No batch stock for this item"
    : `${first.batchNo ? `Batch ${first.batchNo}` : "No batch"}${first.expiryDate ? ` · ${formatExpiry(first.expiryDate)}` : ""}`;
  const tipSub = !first
    ? ""
    : [
        days === null ? "No expiry date" : hint(days),
        `${qtyHere} ${stockUnit} from this batch`,
        allocations.length > 1 ? `+${allocations.length - 1} more batch${allocations.length === 2 ? "" : "es"}` : "",
      ]
        .filter(Boolean)
        .join(" · ");
  const tipNote = level === "expired" ? modeText : picked ? "You chose these batches" : "Sold from the batch that expires first";

  return (
    <>
      <Tip title={tipTitle} subtitle={tipSub} hint={`${tipNote}${canPick ? " · tap to see all batches" : ""}`} placement="top">
      <Chip
        size="small"
        clickable
        onClick={(e) => {
          e.stopPropagation();
          setAnchor(e.currentTarget);
        }}
        icon={
          level === "expired" || level === "near" ? (
            <EventBusyOutlinedIcon style={{ fontSize: 14, color: tone.color }} />
          ) : (
            <EventAvailableOutlinedIcon style={{ fontSize: 14, color: tone.color }} />
          )
        }
        label={label}
        style={{
          alignSelf: "flex-start",
          maxWidth: "100%",
          height: 22,
          fontSize: 11,
          fontWeight: 700,
          background: tone.background,
          color: tone.color,
          border: `1px solid ${tone.border}`,
        }}
      />
      </Tip>
      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{ style: { borderRadius: 14, padding: 14, width: 340, maxWidth: "94vw", boxShadow: "0 20px 40px -12px rgba(15,23,42,0.35)" } }}
      >
        <Typography style={{ fontWeight: 800, color: "#0f172a", fontSize: "0.95rem" }}>{item.itemName}</Typography>
        <Typography style={{ color: "#64748b", fontSize: "0.78rem", marginBottom: 8 }}>
          Selling {qtyText(qty)} {item?.units?.sale?.unit?.abbreviation || ""} -{" "}
          {picked ? "from the batches you chose" : "taken from the earliest-expiring batch first"}
        </Typography>

        {allocations.length === 0 ? (
          <Typography style={{ color: "#475569", fontSize: "0.85rem", padding: "6px 0" }}>
            There is no batch stock for this item.
          </Typography>
        ) : (
          allocations.map((a, i) => {
            const d = daysUntilExpiry(a.expiryDate);
            const t = TONES[d === null ? "none" : d < 0 ? "expired" : d <= nearDays ? "near" : "ok"];
            return (
              <div
                key={`${a.batchNo}-${i}`}
                style={{ display: "flex", alignItems: "center", gap: 8, padding: "7px 0", borderTop: "1px solid #f1f5f9" }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: "0.85rem", color: "#0f172a", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    Batch {a.batchNo || "(none)"}
                  </div>
                  <div style={{ fontSize: "0.75rem", color: t.color, fontWeight: 600 }}>
                    {a.expiryDate ? `${formatExpiry(a.expiryDate)} · ${hint(d)}` : "No expiry date"}
                  </div>
                </div>
                <div style={{ fontWeight: 800, fontSize: "0.9rem", color: "#0f172a" }}>
                  {qtyText(a.qty)} {stockUnit}
                </div>
              </div>
            );
          })
        )}

        {canPick && (
          <Button
            fullWidth
            variant="outlined"
            size="small"
            onClick={() => setPickerOpen(true)}
            disabled={qty <= 0}
            style={{ marginTop: 10, textTransform: "none", fontWeight: 800, borderRadius: 10, color: "#ea580c", borderColor: "#fdba74" }}
          >
            {picked ? "Change batch" : "Choose batch"}
          </Button>
        )}
        {plan.shortfall > 0.0001 && !picked && (
          <Typography style={{ marginTop: 6, fontSize: "0.78rem", color: "#b45309", fontWeight: 600 }}>
            {qtyText(plan.shortfall)} {stockUnit} is more than the batches hold.
          </Typography>
        )}
        {(plan.expiredUsed || plan.blocked) && (
          <Typography style={{ marginTop: 6, fontSize: "0.78rem", color: "#b91c1c", fontWeight: 700 }}>
            {plan.blocked ? "Only expired stock can cover this quantity. " : "This sale uses expired stock. "}
            {modeText}
          </Typography>
        )}
      </Popover>
      {canPick && pickerOpen && (
        <BatchPickerDialog
          open
          onClose={() => setPickerOpen(false)}
          item={item}
          needed={needed}
          stockUnit={stockUnit}
          initialPicks={picked || []}
          onConfirm={setPicks}
        />
      )}
    </>
  );
};

export default CartExpiryInfo;
